import { useContext, useEffect } from 'react'
import { DateTime } from 'luxon'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'
import { digObject } from '@domain-group/javascript-utils'
import { useSetState } from '@domain-group/react-hooks'

import usePublicationEditions from '@hooks/usePublicationEditions'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import PublicationEditionTile from '@components/PublicationEditionTile'

const currentDateTime = DateTime.now()
const todayDateString = currentDateTime.toFormat('yyyy-MM-dd')

const MODAL_KEY = 'SelectPublicationEditionModal'

type DefaultState = {
  selectedEditionId: number | undefined,
}

const defaultState: DefaultState = {
  selectedEditionId: undefined,
}

type ModalPayload = {
  callbacks?: {
    selectPublicationEditionId: (payload: HandleCallbackActionParams) => void,
  },
  currentEditionId: number,
  publicationId: number,
}

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  singleSelect?: boolean,
  showModal: boolean,
}

const SelectPublicationEditionModal = (props: ModalProps) => {
  const { callbacks, singleSelect = false, showModal } = props
  const { closeModal } = callbacks || {}

  const [state, setState] = useSetState(defaultState)
  const { selectedEditionId } = state

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload: ModalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    callbacks: modalPayloadCallbacks,
    currentEditionId,
    publicationId,
  } = modalPayload

  const { selectPublicationEditionId } = modalPayloadCallbacks || {}

  useEffect(() => {
    if (currentEditionId){
      setState({
        selectedEditionId: currentEditionId,
      })
    }
  }, [currentEditionId])

  const publicationEditionsPayload = usePublicationEditions({
    filters: {
      min_publication_date: todayDateString,
      publication_id: publicationId,
    },
    performHttpRequests: !!publicationId,
    requestOptions: {
      include_publication_edition_deadlines: true,
    },
  })

  const { filteredPublicationEditions } = publicationEditionsPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Select" titleSecondLine="Edition" />

      <SidebarModal.Content hasFooter={!singleSelect}>
        <Form>
          <Form.Field label="Select Edition">
            {filteredPublicationEditions.map((publicationEdition) => {
              const isSelected = publicationEdition.id === selectedEditionId

              return (
                <PublicationEditionTile
                  callbacks={{
                    selectPublicationEdition: singleSelect && selectPublicationEditionId
                      ? () => selectPublicationEditionId({
                        // @ts-expect-error TS2741
                        callbacks: {
                          afterAction: closeModal,
                        },
                        entityParams: {
                          publication_id: publicationEdition.publication_id,
                          publication_edition_id: publicationEdition.id,
                        },
                        toastText: 'Selected Publication Edition',
                      })
                      : () => setState({ selectedEditionId: publicationEdition.id }),
                  }}
                  publicationEdition={publicationEdition}
                  isSelected={isSelected}
                  key={publicationEdition.id}
                />
              )
            })}
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      {!singleSelect && (
        <SidebarModal.Footer>
          <Button
            buttonStyle="primaryCreate"
            disabled={!selectedEditionId}
            onClick={selectPublicationEditionId
              ? () => selectPublicationEditionId({
                // @ts-expect-error TS2741
                callbacks: {
                  afterAction: closeModal,
                },
                entityParams: {
                  publication_id: publicationId,
                  publication_edition_id: selectedEditionId,
                },
                toastText: 'Selected Publication Edition',
              })
              : undefined}
            size="large"
          >
            Select Edition
          </Button>
        </SidebarModal.Footer>
      )}
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <SelectPublicationEditionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
